// src/lib/voiceContext.js
// Client-side context builder for Janet voice mode.
// Searches the in-memory entity cache (~5ms) so voice responses have
// rich context without any server round-trips or Haiku pre-flight.

import { entries } from './entityCache'

const STOP_WORDS = new Set([
  'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for',
  'of', 'with', 'about', 'what', 'when', 'who', 'how', 'is', 'are', 'was',
  'were', 'be', 'been', 'have', 'has', 'had', 'do', 'does', 'did', 'will',
  'would', 'could', 'should', 'may', 'might', 'can', 'i', 'my', 'me', 'you',
  'your', 'we', 'our', 'they', 'their', 'it', 'its', 'this', 'that',
  'any', 'all', 'some', 'just', 'up', 'hey', 'janet', 'ok', 'okay',
  'please', 'tell', 'show', 'find', 'get', 'look', 'give', 'need',
  'deal', 'deals', 'project', 'projects', 'contact', 'contacts', 'task',
  'tasks', 'today', 'now', 'here', 'there', 'so', 'if', 'then', 'also',
])

function tokenize(text) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(w => w.length > 1 && !STOP_WORDS.has(w))
}

// Collect all records for a cache key prefix, deduped by id
function getAllCached(prefix) {
  const seen = new Set()
  const results = []
  for (const [key, entry] of entries('list')) {
    if (!key.startsWith(prefix + ':')) continue
    const data = Array.isArray(entry?.data) ? entry.data : []
    for (const item of data) {
      if (item?.id && !seen.has(item.id)) {
        seen.add(item.id)
        results.push(item)
      }
    }
  }
  return results
}

// Score a record against search words across given fields
function scoreRecord(record, fields, words) {
  if (!words.length) return 0
  const combined = fields
    .map(f => String(record[f] || ''))
    .join(' ')
    .toLowerCase()
  let total = 0
  for (const w of words) {
    if (combined.includes(w)) total++
  }
  return total
}

function topMatches(records, fields, words, limit = 5) {
  return records
    .map(r => ({ r, s: scoreRecord(r, fields, words) }))
    .filter(x => x.s > 0)
    .sort((a, b) => b.s - a.s)
    .slice(0, limit)
    .map(x => x.r)
}

// ── Formatters ──────────────────────────────────────────────────────────────

function fmtContact(c) {
  const name  = [c.first_name, c.last_name].filter(Boolean).join(' ') || 'Unknown'
  const co    = c.organization_name ? ` @ ${c.organization_name}` : ''
  const job   = c.job_title ? ` (${c.job_title})` : ''
  const email = c.emails?.[0]?.email || c.email || ''
  const last  = c.last_contact_date ? ` — last contact ${c.last_contact_date}` : ''
  return `  ${name}${co}${job}${email ? ' <' + email + '>' : ''}${last}`
}

function fmtDeal(d) {
  const val   = d.annual_value ? ` — $${Number(d.annual_value).toLocaleString()}/yr` : ''
  const terms = d.key_terms ? `\n    Terms: ${String(d.key_terms).slice(0, 150)}` : ''
  return `  ${d.deal_type || 'Deal'} with ${d.counterparty_name || 'unknown'} — ${d.status || '?'}${val}${terms}`
}

function fmtProject(p) {
  const fmt = p.format ? ` (${p.format})` : ''
  return `  "${p.title || 'Untitled'}" — ${p.status || '?'}${fmt}`
}

function fmtTask(t) {
  const q   = t.quadrant ? `Q${t.quadrant}` : '?'
  const due = t.due_date ? ` due ${t.due_date}` : ''
  return `  [${q}] ${t.title || '?'}${due} — ${t.status || 'open'}`
}

// ── Main export ─────────────────────────────────────────────────────────────

export function buildVoiceContext(message) {
  if (!message) return null
  const words = tokenize(message)

  const today = new Date().toISOString().slice(0, 10)

  const contacts  = getAllCached('contacts')
  const deals     = getAllCached('deals')
  const projects  = getAllCached('projects')
  const tasks     = getAllCached('tasks')

  const matchedContacts  = words.length ? topMatches(contacts, ['first_name', 'last_name', 'organization_name', 'job_title'], words) : []
  const matchedDeals     = words.length ? topMatches(deals, ['deal_type', 'counterparty_name', 'key_terms', 'notes', 'status'], words) : []
  const matchedProjects  = words.length ? topMatches(projects, ['title', 'format', 'status'], words) : []

  // Tasks: always surface today's open tasks; also score against words
  const todayTasks = tasks.filter(t => t.due_date === today && t.status !== 'completed')
  const scoredTasks = words.length
    ? tasks
        .filter(t => !todayTasks.find(x => x.id === t.id))
        .map(t => ({ t, s: scoreRecord(t, ['title', 'description'], words) }))
        .filter(x => x.s > 0)
        .sort((a, b) => b.s - a.s)
        .slice(0, 5)
        .map(x => x.t)
    : []
  const allTasks = [...todayTasks, ...scoredTasks].slice(0, 8)

  const parts = []
  if (matchedContacts.length) parts.push('CONTACTS:\n' + matchedContacts.map(fmtContact).join('\n'))
  if (matchedDeals.length)    parts.push('DEALS:\n'    + matchedDeals.map(fmtDeal).join('\n'))
  if (matchedProjects.length) parts.push('PROJECTS:\n' + matchedProjects.map(fmtProject).join('\n'))
  if (allTasks.length)        parts.push('TASKS:\n'    + allTasks.map(fmtTask).join('\n'))

  if (!parts.length) return null
  return parts.join('\n\n')
}
